import React, { useEffect, useState } from 'react';
import api from '../api/axios';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, CircularProgress, Alert, List, ListItem, ListItemText, IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useRef } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import BreadcrumbsNav from '../components/BreadcrumbsNav';

const ConverterDetailPage = () => {
  const { projectId, converterId } = useParams();
  const navigate = useNavigate();
  const [converter, setConverter] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef();
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renameFileId, setRenameFileId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editFields, setEditFields] = useState(['']);
  const [editError, setEditError] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  const fetchConverter = async () => {
    setLoading(true);
    setError('');
    try {
      // Fetch all converters for the project, then find the one we want
      const res = await api.get(`/converters/${projectId}`);
      setConverter(res.data.find(c => c._id === converterId));
    } catch (err) {
      setError('Failed to load converter');
    }
    setLoading(false);
  };

  const fetchFiles = async () => {
    try {
      const res = await api.get(`/files/list/${projectId}/${converterId}`);
      setFiles(res.data.files);
    } catch {
      setFiles([]);
    }
  };

  useEffect(() => {
    fetchConverter();
    fetchFiles();
    // eslint-disable-next-line
  }, [projectId, converterId]);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setError('');
    const formData = new FormData();
    formData.append('file', file);
    try {
      await api.post(`/files/${projectId}/${converterId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      fetchFiles();
    } catch (err) {
      setError('Upload failed');
    }
    setUploading(false);
    fileInputRef.current.value = '';
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('Delete this file?')) return;
    setDeleting(true);
    try {
      await api.delete(`/files/${fileId}`);
      fetchFiles();
    } catch {
      setError('Failed to delete file');
    }
    setDeleting(false);
  };

  const openRenameDialog = (file) => {
    setRenameFileId(file._id);
    setRenameValue(file.originalName);
    setRenameDialogOpen(true);
  };

  const handleRenameFile = async () => {
    try {
      await api.put(`/files/${renameFileId}`, { originalName: renameValue });
      setRenameDialogOpen(false);
      fetchFiles();
    } catch {
      setError('Failed to rename file');
    }
  };

  const handleEditClick = () => {
    setEditMode(true);
    setEditName(converter?.name || '');
    setEditDescription(converter?.description || '');
    setEditFields(converter?.fields?.length ? [...converter.fields] : ['']);
    setEditError('');
  };

  const handleEditFieldChange = (idx, value) => {
    setEditFields(prev => prev.map((f, i) => (i === idx ? value : f)));
  };

  const handleEditAddField = () => {
    setEditFields(prev => [...prev, '']);
  };

  const handleEditRemoveField = (idx) => {
    setEditFields(prev => prev.filter((_, i) => i !== idx));
  };

  const handleEditCancel = () => {
    setEditMode(false);
    setEditError('');
  };

  const handleEditSave = async () => {
    setEditSaving(true);
    setEditError('');
    try {
      await api.put(`/converters/${converterId}`, {
        name: editName,
        description: editDescription,
        fields: editFields.filter(f => f.trim()),
        exportSettings: converter.exportSettings
      });
      setEditMode(false);
      fetchConverter();
    } catch (err) {
      setEditError('Failed to save changes');
    }
    setEditSaving(false);
  };

  return (
    <Box maxWidth={700} mx="auto" mt={4}>
      <BreadcrumbsNav />
      {loading ? <CircularProgress /> : converter && (
        <>
          {!editMode ? (
            <>
              <Box display="flex" alignItems="center" gap={2}>
                <Typography variant="h5" mt={2}>{converter.name}</Typography>
                <IconButton onClick={handleEditClick} size="small"><EditIcon /></IconButton>
              </Box>
              <Typography variant="subtitle1" mb={2}>{converter.description}</Typography>
              <Typography variant="body2" mb={2}>Fields: {converter.fields.join(', ')}</Typography>
            </>
          ) : (
            <Box display="flex" flexDirection="column" gap={2} mt={2} mb={2}>
              <TextField label="Name" value={editName} onChange={e => setEditName(e.target.value)} required />
              <TextField label="Description" value={editDescription} onChange={e => setEditDescription(e.target.value)} />
              <Box>
                <Typography variant="subtitle2" mb={1}>Fields</Typography>
                {editFields.map((field, idx) => (
                  <Box key={idx} display="flex" alignItems="center" mb={1} gap={1}>
                    <TextField
                      label={`Field ${idx + 1}`}
                      value={field}
                      onChange={e => handleEditFieldChange(idx, e.target.value)}
                      required
                      size="small"
                    />
                    <IconButton onClick={() => handleEditRemoveField(idx)} disabled={editFields.length === 1} size="small">
                      <RemoveIcon />
                    </IconButton>
                    {idx === editFields.length - 1 && (
                      <IconButton onClick={handleEditAddField} size="small">
                        <AddIcon />
                      </IconButton>
                    )}
                  </Box>
                ))}
              </Box>
              <Box display="flex" gap={2}>
                <Button variant="contained" onClick={handleEditSave} disabled={editSaving}>Save</Button>
                <Button variant="outlined" color="secondary" onClick={handleEditCancel}>Cancel</Button>
              </Box>
              {editError && <Alert severity="error">{editError}</Alert>}
            </Box>
          )}
        </>
      )}
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      <Box mt={3} mb={2}>
        <input
          type="file"
          accept=".pdf,.docx,.xlsx,.jpg,.jpeg,.png"
          style={{ display: 'none' }}
          ref={fileInputRef}
          onChange={handleFileChange}
        />
        <Button
          variant="contained"
          startIcon={<UploadFileIcon />}
          onClick={() => fileInputRef.current.click()}
          disabled={uploading}
        >
          {uploading ? 'Uploading...' : 'Upload File'}
        </Button>
      </Box>
      <Typography variant="h6" mt={3}>Uploaded Files</Typography>
      <List>
        {files.map(file => (
          <ListItem key={file._id} button onClick={() => navigate(`/project/${projectId}/converter/${converterId}/file/${file._id}`)}>
            <ListItemText
              primary={file.originalName}
              secondary={`Uploaded: ${new Date(file.createdAt).toLocaleString()}`}
            />
            <IconButton edge="end" aria-label="edit" onClick={e => { e.stopPropagation(); openRenameDialog(file); }}>
              <EditIcon />
            </IconButton>
            <IconButton edge="end" aria-label="delete" onClick={e => { e.stopPropagation(); handleDeleteFile(file._id); }} disabled={deleting}>
              <DeleteIcon />
            </IconButton>
          </ListItem>
        ))}
      </List>
      <Dialog open={renameDialogOpen} onClose={() => setRenameDialogOpen(false)}>
        <DialogTitle>Rename File</DialogTitle>
        <DialogContent>
          <TextField
            label="New Name"
            value={renameValue}
            onChange={e => setRenameValue(e.target.value)}
            fullWidth
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleRenameFile} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConverterDetailPage; 